import React, { useEffect, useRef, useState } from "react";
import {Animated, Easing, Pressable, StyleSheet, Text, View} from "react-native";
import {Pause, Play, RotateCcw, RotateCw, SkipBack, SkipForward, Volume2, VolumeX} from "lucide-react-native";
import {PlayerButton} from "./PlayerPrimitives";
import {playerFont} from "../../theme/tokens";
import {isTelevisionRuntime} from "../../utils/platformCapabilities";
import {mediaVolumeToSliderPosition, sliderPositionToMediaVolume} from "../../player/volumeCurve";

type VolumePointerEvent = {
  nativeEvent: {
    locationX?: number;
    offsetX?: number;
    pageX?: number;
    stopPropagation?: () => void;
  };
};

export function PlayerTransportControls({ compact, shortLandscape, paused, muted, volume, position, seekBackSeconds, seekForwardSeconds, title, hasPrevious, hasNext, isEnabled, onTogglePaused, onToggleMuted, onSeek, onPrevious, onNext, onAdjustVolume, onSetVolume, onFocusChange, onActivity }: { compact: boolean; shortLandscape: boolean; paused: boolean; muted: boolean; volume: number; position: number; seekBackSeconds: number; seekForwardSeconds: number; title: string; hasPrevious: boolean; hasNext: boolean; isEnabled: (key: string) => boolean; onTogglePaused: () => void; onToggleMuted: () => void; onSeek: (seconds: number) => void; onPrevious: () => void; onNext: () => void; onAdjustVolume: (direction: -1 | 1) => void; onSetVolume: (volume: number) => void; onFocusChange: (focused: boolean) => void; onActivity: () => void }) {
  const television = isTelevisionRuntime();
  const volumeWidth = useRef(84);
  const volumeTrack = useRef<React.ElementRef<typeof Pressable>>(null);
  const rafRef = useRef<number>(0);
  const pendingVolume = useRef<number | null>(null);
  const volumeCollapseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const previousVolume = useRef(volume);
  const [volumeActive, setVolumeActive] = useState(false);
  const volumeReveal = useRef(new Animated.Value(0)).current;
  // Keep every transport control vertically connected to the timeline on TV.
  // Native focus geometry otherwise lets the page/overlay consume the first
  // D-pad move before the player graph gets a chance to seek.
  const buttonProps = { onFocusChange, onActivity, dense: shortLandscape, focusDestinations: { up: "player.timeline" } };
  const showTransportCore = isEnabled("seekBackward") || isEnabled("playPause") || isEnabled("seekForward");
  const clampVolumeRatio = (value: number) => Math.max(0, Math.min(1, value));
  const volumeSliderPosition = muted ? 0 : mediaVolumeToSliderPosition(volume);

  useEffect(() => () => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
    }
    if (volumeCollapseTimer.current) clearTimeout(volumeCollapseTimer.current);
  }, []);
  const revealVolume = () => {
    if (volumeCollapseTimer.current) clearTimeout(volumeCollapseTimer.current);
    setVolumeActive(true);
  };
  const collapseVolumeSoon = (delay = 180) => {
    if (volumeCollapseTimer.current) clearTimeout(volumeCollapseTimer.current);
    volumeCollapseTimer.current = setTimeout(() => setVolumeActive(false), delay);
  };
  useEffect(() => {
    Animated.timing(volumeReveal, { toValue: volumeActive ? 1 : 0, duration: 150, easing: Easing.out(Easing.cubic), useNativeDriver: false }).start();
  }, [volumeActive, volumeReveal]);
  useEffect(() => {
    if (previousVolume.current === volume) return;
    previousVolume.current = volume;
    revealVolume();
    collapseVolumeSoon(950);
  }, [volume]);

  const flushVolume = () => {
    const next = pendingVolume.current;
    pendingVolume.current = null;
    if (next == null) return;
    onSetVolume(next);
  };

  const queueVolume = (target: number) => {
    pendingVolume.current = sliderPositionToMediaVolume(clampVolumeRatio(target));
    if (rafRef.current) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = 0;
      flushVolume();
    });
  };

  const applyVolumeFromEvent = (event: VolumePointerEvent) => {
    const locationX = Number(event?.nativeEvent?.locationX);
    if (Number.isFinite(locationX)) {
      queueVolume(locationX / Math.max(1, volumeWidth.current));
      return;
    }

    const offsetX = Number(event?.nativeEvent?.offsetX);
    if (Number.isFinite(offsetX)) {
      queueVolume(offsetX / Math.max(1, volumeWidth.current));
      return;
    }

    const pointerX = Number(event?.nativeEvent?.pageX);
    if (!Number.isFinite(pointerX)) return;
    volumeTrack.current?.measure((_x, _y, measuredWidth, _height, pageX) => {
      if (!Number.isFinite(pageX)) return;
      queueVolume((pointerX - pageX) / Math.max(1, measuredWidth));
    });
  };
  return (
    <>
      <View style={[styles.transportGroup, !compact && styles.transportActions, compact && styles.transportGroupCompact, shortLandscape && styles.transportGroupShort]}>
        {showTransportCore ? <View style={[styles.transportCore, shortLandscape && styles.transportCoreShort, television && styles.transportCoreTelevision]}>
          {isEnabled("seekBackward") ? <PlayerButton {...buttonProps} quiet label={`Seek back ${seekBackSeconds} seconds`} onPress={() => onSeek(position - seekBackSeconds)}><RotateCcw color="#fff" size={television ? 24 : 20} /></PlayerButton> : null}
          {isEnabled("playPause") ? <PlayerButton {...buttonProps} focusId="player.play" label={paused ? "Play" : "Pause"} onPress={onTogglePaused} primary>{paused ? <Play color="#fff" fill="#fff" size={television ? 31 : shortLandscape ? 18 : compact ? 23 : 26} /> : <Pause color="#fff" size={television ? 31 : shortLandscape ? 18 : compact ? 23 : 26} />}</PlayerButton> : null}
          {isEnabled("seekForward") ? <PlayerButton {...buttonProps} quiet label={`Seek forward ${seekForwardSeconds} seconds`} onPress={() => onSeek(position + seekForwardSeconds)}><RotateCw color="#fff" size={television ? 24 : 20} /></PlayerButton> : null}
        </View> : null}
        {(hasPrevious && isEnabled("previousEpisode")) || (hasNext && isEnabled("nextEpisode")) ? <View style={[styles.episodeNavigation, shortLandscape && styles.episodeNavigationShort]}>
          {hasPrevious && isEnabled("previousEpisode") ? <PlayerButton {...buttonProps} squircle quiet label="Previous episode" onPress={onPrevious}><SkipBack color="#fff" size={television ? 22 : 18} /></PlayerButton> : null}
          {hasNext && isEnabled("nextEpisode") ? <PlayerButton {...buttonProps} squircle quiet label="Next episode" onPress={onNext}><SkipForward color="#fff" size={television ? 22 : 18} /></PlayerButton> : null}
        </View> : null}
        {isEnabled("mute") ? <PlayerButton {...buttonProps} quiet onHoverChange={(hovered) => { if (hovered) revealVolume(); else collapseVolumeSoon(); }} onFocusChange={(focused) => { onFocusChange(focused); if (focused) revealVolume(); else collapseVolumeSoon(); }} label={muted ? "Unmute" : "Mute"} onPress={onToggleMuted}>{muted ? <VolumeX color="#fff" size={television ? 24 : 20} /> : <Volume2 color="#fff" size={television ? 24 : 20} />}</PlayerButton> : null}
        {!shortLandscape && isEnabled("volumeSlider") ? (
          <Animated.View pointerEvents={volumeActive ? "auto" : "none"} style={[styles.volumeReveal, { width: volumeReveal.interpolate({ inputRange: [0, 1], outputRange: [0, 84] }), opacity: volumeReveal }]}><Pressable
            accessibilityLabel="Volume"
            accessibilityRole="adjustable"
            focusable={volumeActive}
            accessibilityValue={{ min: 0, max: 100, now: Math.round(volumeSliderPosition * 100), text: `${Math.round(volumeSliderPosition * 100)} percent` }}
            accessibilityActions={[{ name: "increment", label: "Increase volume" }, { name: "decrement", label: "Decrease volume" }]}
            onAccessibilityAction={(event) => { if (event.nativeEvent.actionName === "increment") onAdjustVolume(1); else if (event.nativeEvent.actionName === "decrement") onAdjustVolume(-1); }}
            onFocus={() => { revealVolume(); onFocusChange(true); onActivity(); }}
            onBlur={() => { onFocusChange(false); collapseVolumeSoon(); }}
            onHoverIn={() => { revealVolume(); onActivity(); }}
            onHoverOut={() => collapseVolumeSoon()}
            onPressIn={(event) => { event.stopPropagation(); applyVolumeFromEvent(event); onActivity(); }}
            onPress={(event) => { event.stopPropagation(); applyVolumeFromEvent(event); flushVolume(); }}
            onResponderMove={(event) => applyVolumeFromEvent(event as VolumePointerEvent)}
            onResponderRelease={flushVolume}
            onResponderTerminate={flushVolume}
            onPressOut={flushVolume}
            onLayout={(event) => { volumeWidth.current = Math.max(1, event.nativeEvent.layout.width); }}
            ref={volumeTrack}
            style={styles.volumeHitbox}
          >
            <View pointerEvents="none" style={styles.volumeTrack}><View style={[styles.volumeFilled, { flex: Math.max(0.001, volumeSliderPosition) }]} /><View style={{ flex: Math.max(0.001, 1 - volumeSliderPosition) }} /></View>
            <View pointerEvents="none" style={styles.volumeThumbRow}><View style={{ flex: Math.max(0.001, volumeSliderPosition) }} /><View style={styles.volumeThumb} /><View style={{ flex: Math.max(0.001, 1 - volumeSliderPosition) }} /></View>
          </Pressable></Animated.View>
        ) : null}
        {!compact && !shortLandscape && isEnabled("inlineTitle") ? <Text numberOfLines={1} style={[styles.inlineTitle, television && styles.inlineTitleTelevision]}>{title}</Text> : null}
      </View>
      {compact && isEnabled("inlineTitle") ? <Text numberOfLines={1} style={styles.inlineTitleCompact}>{title}</Text> : null}
    </>
  );
}

const styles = StyleSheet.create({
  transportGroup: { flexDirection: "row", alignItems: "center", gap: 2 },
  transportCore: { minHeight: 50, paddingHorizontal: 4, paddingVertical: 2, borderWidth: 1, borderColor: "rgba(255,255,255,0.10)", borderRadius: 25, flexDirection: "row", alignItems: "center", gap: 1, backgroundColor: "rgba(255,255,255,0.055)" },
  transportCoreShort: { minHeight: 32, paddingHorizontal: 2, paddingVertical: 1, borderRadius: 16 },
  transportCoreTelevision: { minHeight: 58, paddingHorizontal: 5, borderRadius: 29 },
  episodeNavigation: { marginLeft: 5, padding: 2, borderWidth: 1, borderColor: "rgba(255,255,255,0.08)", borderRadius: 11, flexDirection: "row", alignItems: "center", gap: 1, backgroundColor: "rgba(255,255,255,0.035)" },
  episodeNavigationShort: { marginLeft: 2, padding: 1, borderRadius: 8 },
  transportActions: { minWidth: 0, flex: 1 },
  transportGroupCompact: { minHeight: 40, alignSelf: "stretch", flexWrap: "wrap", justifyContent: "center" },
  transportGroupShort: { minWidth: 0, flex: 1, gap: 0 },
  inlineTitle: { minWidth: 0, flexShrink: 1, marginLeft: 12, color: "#fff", fontFamily: playerFont, fontSize: 14, lineHeight: 18, fontWeight: "500" },
  inlineTitleTelevision: { marginLeft: 16, fontSize: 17, lineHeight: 22 },
  inlineTitleCompact: { alignSelf: "stretch", color: "#fff", fontFamily: playerFont, fontSize: 12, lineHeight: 16, fontWeight: "500", textAlign: "center" },
  volumeHitbox: { width: 84, height: 32, justifyContent: "center" },
  volumeReveal: { overflow: "hidden" },
  volumeTrack: { height: 4, borderRadius: 4, flexDirection: "row", overflow: "hidden", backgroundColor: "rgba(255,255,255,0.26)" },
  volumeFilled: { backgroundColor: "#e50914" },
  volumeThumbRow: { position: "absolute", left: 0, right: 0, top: 8, height: 16, flexDirection: "row", alignItems: "center" },
  volumeThumb: { width: 16, height: 16, borderWidth: 1, borderColor: "rgba(255,255,255,0.92)", borderRadius: 8, backgroundColor: "#8b8b8b", shadowColor: "#000", shadowOpacity: 0.62, shadowRadius: 4, shadowOffset: { width: 0, height: 1 }, elevation: 4 },
});
